import React from 'react'

import {
  StyledPopover,
  StyledOption,
  StyledOptionDelete,
  SelectOptionDeleteWrapper,
  SelectOptionWrapper,
  Checkmark,
  SecondaryText,
  Separator,
  FullSeparator,
  CommentDropdownItem,
  Checkbox,
  ToggleWrapper,
  IconWrapper,
  StyledChevron,
} from './AccessLevelSelect.styles'

import {
  AccessLevelSelectProps,
  WorkspaceAccessLevel,
  PrivateOrWorkspaceAccessLevel,
  DropdownTitleProps,
  PublicAccess,
  PrivateAccess,
  WorkspaceAccess,
  InviteStatus,
} from './types'

import {
  SelectTriggerUnstyled,
  SelectInput,
  SelectList,
} from 'components/Select'
// Usage of PublicAccessLevel and PrivateAccessLevel will not break the build
// eslint-disable-next-line no-restricted-imports
import { PublicAccessLevel, PrivateAccessLevel } from 'gqlTypes/expansiveTypes'

/**
 * In order to position the Select popover we can use a function, it's not documented
 * in Reach UI docs but the source code is quite readable so if you need more info
 * about how to position the popover please check this:
 * https://github.com/reach/reach-ui/blob/01c1a16315e7d89f7e7f338cb8c3d2a72023087c/packages/popover/src/index.tsx#L140
 */
import { positionRight } from '@reach/popover'
import { useListboxContext } from '@reach/listbox'

export const isLevelDisabled = (
  level: PrivateAccessLevel,
  access: PublicAccess | PrivateAccess | WorkspaceAccess
): boolean => {
  if (access.kind !== 'private') {
    return false
  }

  return !access.allowedLevels.includes(level)
}

export const hasRequestedAccess = (inviteStatus?: InviteStatus) =>
  inviteStatus === 'REQUESTED'

export const getTitle = (access: AccessLevelSelectProps['access']): string => {
  const defaultsAccessLevelTitles = {
    NONE: 'No Access',
    VIEW: 'View',
    EDIT: 'Edit',
    INSPECT: 'Inspect',
    PENDING: 'Pending',
    DEFAULT: 'Default',
  }

  return defaultsAccessLevelTitles[access.level]
}

export const DropdownTitle: React.FC<DropdownTitleProps> = ({
  children,
  isEmphasized = false,
  disabled,
}) => (
  <ToggleWrapper
    data-testid="access-level-dropdown-toggle"
    isEmphasized={isEmphasized}
    disabled={disabled}>
    {children}
    <IconWrapper>
      <StyledChevron />
    </IconWrapper>
  </ToggleWrapper>
)

interface SelectTriggerProps {
  title: string
  onEnterClicked?: () => void
  disabled?: boolean
  isEmphasized: boolean
}

const SelectTrigger = ({
  onEnterClicked,
  disabled,
  title,
  isEmphasized,
}: SelectTriggerProps) => {
  const { isExpanded } = useListboxContext()

  return (
    <SelectTriggerUnstyled
      aria-disabled={disabled}
      onKeyDown={e => {
        if (e.key === 'Enter' && !isExpanded) {
          onEnterClicked?.()
        }
      }}>
      <DropdownTitle isEmphasized={isEmphasized}>{title}</DropdownTitle>
    </SelectTriggerUnstyled>
  )
}

function isPublicAccessLevel(value: string): value is PublicAccessLevel {
  const inspect: PublicAccessLevel = 'INSPECT'
  const none: PublicAccessLevel = 'NONE'
  const view: PublicAccessLevel = 'VIEW'
  return value === inspect || value === none || value === view
}
function isPrivateAccessLevel(value: string): value is PrivateAccessLevel {
  const inspect: PrivateAccessLevel = 'INSPECT'
  const edit: PrivateAccessLevel = 'EDIT'
  const view: PrivateAccessLevel = 'VIEW'
  return value === inspect || value === edit || value === view
}
function isWorkspaceAccessLevel(value: string): value is WorkspaceAccessLevel {
  const inspectLevel: WorkspaceAccessLevel = 'INSPECT'
  const editLevel: WorkspaceAccessLevel = 'DEFAULT'
  const viewLevel: WorkspaceAccessLevel = 'VIEW'
  return value === inspectLevel || value === editLevel || value === viewLevel
}
const getHelpText = (
  kind: string,
  accessLevel: PrivateOrWorkspaceAccessLevel
) =>
  kind === 'workspace'
    ? {
        VIEW: 'View document in the web app',
        INSPECT: 'View, inspect and download document in the web app',
        EDIT: 'Workspace Editors can edit and Viewers can inspect',
        DEFAULT: 'Workspace Editors can edit and Viewers can inspect',
      }[accessLevel as WorkspaceAccessLevel]
    : {
        VIEW: 'View document in the web app',
        INSPECT: 'View, inspect and download document in the web app',
        EDIT: 'All of the above, and edit document in the Mac app',
        DEFAULT: 'All of the above, and edit document in the Mac app',
      }[accessLevel as PrivateAccessLevel]

const AccessLevelSelect: React.FC<AccessLevelSelectProps> = ({
  access,
  disabled,
  commentsEnabled,
  setCommentsEnabled,
  canUpdateCommentsEnabled,
  onEnterClicked,
  inviteStatus,
  deleteMember,
  isInView,
}) => {
  const title = getTitle(access)
  const pending = access.level === 'PENDING'
  const remove = hasRequestedAccess(inviteStatus)
    ? 'Deny Access to Document'
    : 'Remove from Document'

  return (
    <SelectInput
      // To solve https://github.com/sketch-hq/Cloud/issues/8122 we are using
      // intersection observer to check when the list item is out of view.
      // Since reach-ui ListboxPopover doesn't have a method to close the popover that we can use,
      // we are setting a "key" prop to the parent of the popover to force it to rerender.
      // This is a hack that forces the popover to close and stay closed when scrolling the members list.
      key={`isInView-${!!isInView}`}
      value={access.level}
      onChange={value => {
        // If the value is the same there shouldn't happen any action
        if (value === access.level) {
          return
        }

        // The select's value is just typed as string for now, so have to
        // jump through some hoops to call setLevel in as type safe a manner
        // as possible:
        switch (access.kind) {
          case 'public':
            if (isPublicAccessLevel(value)) {
              access.setLevel(value)
            }
            break
          case 'private':
            if (value === 'REMOVE' && deleteMember) {
              deleteMember()
              return
            }
            if (isPrivateAccessLevel(value)) {
              access.setLevel(value)
            }
            break
          case 'workspace':
            if (isWorkspaceAccessLevel(value)) {
              access.setLevel(value)
            }
            break
        }
      }}>
      <SelectTrigger
        onEnterClicked={onEnterClicked}
        disabled={disabled}
        title={title}
        isEmphasized={pending}
      />
      <StyledPopover position={positionRight}>
        <SelectList>
          {access.kind === 'public' && (
            <StyledOption value="NONE" label="No Access">
              {title === 'No Access' && <Checkmark />}
              <SelectOptionWrapper>
                No Access
                <SecondaryText>Remove access by link</SecondaryText>
              </SelectOptionWrapper>
              <Separator />
            </StyledOption>
          )}

          <StyledOption
            name="View"
            value="VIEW"
            label="View"
            disabled={isLevelDisabled('VIEW', access)}>
            {title === 'View' && <Checkmark />}
            <SelectOptionWrapper>
              View
              <SecondaryText>{getHelpText(access.kind, 'VIEW')}</SecondaryText>
            </SelectOptionWrapper>
            <Separator />
          </StyledOption>

          <StyledOption
            value="INSPECT"
            label="Inspect"
            disabled={isLevelDisabled('INSPECT', access)}>
            {title === 'Inspect' && <Checkmark />}
            <SelectOptionWrapper>
              Inspect
              <SecondaryText>
                {getHelpText(access.kind, 'INSPECT')}
              </SecondaryText>
            </SelectOptionWrapper>
            {(access.kind === 'private' || access.kind === 'workspace') && (
              <Separator />
            )}
          </StyledOption>

          {access.kind === 'private' && (
            <StyledOption
              value="EDIT"
              label="Edit"
              disabled={isLevelDisabled('EDIT', access)}
              data-testid="access-level-dropdown-edit">
              {title === 'Edit' && <Checkmark />}
              <SelectOptionWrapper>
                Edit
                {isLevelDisabled('EDIT', access) ? (
                  <SecondaryText>Only available for Workspaces</SecondaryText>
                ) : (
                  <SecondaryText>
                    {getHelpText(access.kind, 'EDIT')}
                  </SecondaryText>
                )}
              </SelectOptionWrapper>
            </StyledOption>
          )}

          {access.kind === 'workspace' && (
            <StyledOption
              value="DEFAULT"
              label="Default"
              data-testid="access-level-dropdown-edit">
              {title === 'Default' && <Checkmark />}
              <SelectOptionWrapper>
                Default
                <SecondaryText>
                  {getHelpText(access.kind, 'DEFAULT')}
                </SecondaryText>
              </SelectOptionWrapper>
            </StyledOption>
          )}

          {access.kind === 'private' && deleteMember && (
            <>
              <FullSeparator />
              <StyledOptionDelete
                value="REMOVE"
                label={remove}
                data-testid="access-level-dropdown-delete-member">
                <SelectOptionDeleteWrapper>{remove}</SelectOptionDeleteWrapper>
              </StyledOptionDelete>
            </>
          )}

          {!pending && (
            <>
              <FullSeparator />

              <CommentDropdownItem
                onClick={(event: React.SyntheticEvent) => {
                  event.stopPropagation()
                }}>
                <Checkbox
                  disabled={!canUpdateCommentsEnabled}
                  data-testid="comments-enabled"
                  onClick={event => {
                    event.stopPropagation()
                  }}
                  checked={commentsEnabled}
                  onChange={event => {
                    setCommentsEnabled((event.target || null)?.checked ?? false)
                  }}>
                  Allow commenting
                </Checkbox>
              </CommentDropdownItem>
            </>
          )}
        </SelectList>
      </StyledPopover>
    </SelectInput>
  )
}
export default AccessLevelSelect
